import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface FiltroTipoConfig {
  label: string;
  options: string[];
}

interface FiltroTipoDB {
  id: number;
  label: string;
  activo: boolean;
  orden: number;
}

interface FiltroOpcionDB {
  id: number;
  filtro_tipo_id: number;
  nombre: string;
  activo: boolean;
  orden: number;
}

interface NoticiasFiltersProps {
  onFilterChange?: (filters: Record<string, string>) => void;
}

const defaultFiltros: FiltroTipoConfig[] = [
  { label: "Segmento", options: ["Inauguración", "Extensión", "Avances", "Actividades", "Alianzas"] },
  { label: "Comuna", options: ["Iquique", "Alto Hospicio", "Pica", "Pozo Almonte", "Huara", "Camiña", "Colchane"] },
  { label: "Fecha", options: ["2026", "2025", "2024"] },
];

export default function NoticiasFilters({ onFilterChange }: NoticiasFiltersProps) {
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [filtros, setFiltros] = useState<FiltroTipoConfig[]>(defaultFiltros);

  useEffect(() => {
    const fetchFiltros = async () => {
      try {
        const { data: tipos, error: tiposErr } = await supabase
          .from("noticias_filtros_tipos")
          .select("*")
          .eq("activo", true)
          .order("orden");

        if (tiposErr) throw tiposErr;
        if (!tipos || tipos.length === 0) {
          setFiltros(defaultFiltros);
          return;
        }

        const { data: opciones, error: opcionesErr } = await supabase
          .from("noticias_filtros_opciones")
          .select("*")
          .eq("activo", true)
          .order("orden");

        if (opcionesErr) throw opcionesErr;

        const config: FiltroTipoConfig[] = tipos.map((tipo: FiltroTipoDB) => ({
          label: tipo.label,
          options: (opciones || [])
            .filter((op: FiltroOpcionDB) => op.filtro_tipo_id === tipo.id)
            .map((op: FiltroOpcionDB) => op.nombre),
        }));

        setFiltros(config.length > 0 ? config : defaultFiltros);
      } catch {
        setFiltros(defaultFiltros);
      }
    };

    fetchFiltros();
  }, []);

  const handleChange = (label: string, value: string) => {
    const newFilters = { ...selected, [label]: value };
    if (!value) {
      delete newFilters[label];
    }
    setSelected(newFilters);
    onFilterChange?.(newFilters);
  };

  if (filtros.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3">
      {/* Botón filtros cuadrado */}
      <button
        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-white border border-masmed-gray-300 rounded-lg shadow-sm hover:border-primary hover:text-primary transition-all duration-200 cursor-pointer flex-shrink-0"
        aria-label="Filtros avanzados"
      >
        <i className="ri-equalizer-line text-masmed-gray-600 text-base md:text-lg" />
      </button>

      {filtros.map((filtro) => (
        <div key={filtro.label} className="relative">
          <select
            value={selected[filtro.label] || ""}
            onChange={(e) => handleChange(filtro.label, e.target.value)}
            className="appearance-none bg-white border border-masmed-gray-300 rounded-lg shadow-sm px-3 md:px-4 py-2 md:py-2 pr-7 md:pr-8 text-sm text-masmed-gray-700 focus:outline-none focus:border-primary cursor-pointer min-w-[110px]"
          >
            <option value="">{filtro.label}</option>
            {filtro.options.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-2 md:right-2.5 top-1/2 -translate-y-1/2 text-masmed-gray-400 pointer-events-none text-base" />
        </div>
      ))}
    </div>
  );
}